import { HttpError } from '@/core/api/http-client'
import type { TableLayout } from '@/domain/entities/table-layout.entity'
import type { TableLayoutRepository } from '@/domain/repositories/table-layout.repository'

/**
 * Implementasi sementara selama endpoint /table-layout belum ada di BE.
 * Menyimpan seluruh denah per outlet ke localStorage.
 */
const KEY = 'pos-dashboard:table-layout:'
const PATH = '/table-layout'

function conflict(message: string): HttpError {
  return new HttpError(409, 'Conflict', PATH, message)
}

export class TableLayoutMockRepository implements TableLayoutRepository {
  async get(outletId: string): Promise<TableLayout> {
    return this.read(outletId)
  }

  async save(layout: TableLayout): Promise<TableLayout> {
    const current = this.read(layout.outletId)

    if (current.version !== layout.version) {
      throw conflict('Denah sudah diubah dari perangkat lain.')
    }

    assertUniqueAreaNames(layout)
    assertUniqueTableNames(layout)

    const saved: TableLayout = { ...layout, version: current.version + 1 }
    localStorage.setItem(`${KEY}${layout.outletId}`, JSON.stringify(saved))
    return saved
  }

  async updateOccupancy(outletId: string, tableId: string, isOccupied: boolean): Promise<void> {
    const current = this.read(outletId)
    for (const area of current.areas) {
      for (const table of area.tables) {
        if (table.id === tableId) {
          ;(table as any).isOccupied = isOccupied
        }
      }
    }
    localStorage.setItem(`${KEY}${outletId}`, JSON.stringify(current))
  }

  async regenerateStaticQR(outletId: string, tableId: string): Promise<{ token: string }> {
    const token = 'st_mock_' + Math.random().toString(36).substring(2, 10)
    const current = this.read(outletId)
    for (const area of current.areas) {
      for (const table of area.tables) {
        if (table.id === tableId) {
          ;(table as any).staticQrToken = token
        }
      }
    }
    localStorage.setItem(`${KEY}${outletId}`, JSON.stringify(current))
    return { token }
  }

  async generateDynamicQR(
    outletId: string,
    tableId: string,
    durationMinutes = 120,
  ): Promise<{ token: string; expires_at: string }> {
    const token = 'dy_mock_' + Math.random().toString(36).substring(2, 10)
    const expires_at = new Date(Date.now() + durationMinutes * 60000).toISOString()
    const current = this.read(outletId)
    for (const area of current.areas) {
      for (const table of area.tables) {
        if (table.id === tableId) {
          ;(table as any).dynamicQrToken = token
          ;(table as any).dynamicQrExpiresAt = expires_at
          ;(table as any).isOccupied = true
          ;(table as any).isDynamicQrActive = true
        }
      }
    }
    localStorage.setItem(`${KEY}${outletId}`, JSON.stringify(current))
    return { token, expires_at }
  }

  async invalidateDynamicQR(outletId: string, tableId: string): Promise<void> {
    const current = this.read(outletId)
    for (const area of current.areas) {
      for (const table of area.tables) {
        if (table.id === tableId) {
          ;(table as any).dynamicQrToken = undefined
          ;(table as any).dynamicQrExpiresAt = undefined
          ;(table as any).isOccupied = false
          ;(table as any).isDynamicQrActive = false
        }
      }
    }
    localStorage.setItem(`${KEY}${outletId}`, JSON.stringify(current))
  }

  private read(outletId: string): TableLayout {
    try {
      const raw = localStorage.getItem(`${KEY}${outletId}`)
      if (raw) return JSON.parse(raw) as TableLayout
    } catch {
      /* penyimpanan rusak — perlakukan seperti denah kosong */
    }
    return { outletId, version: 0, areas: [] }
  }
}

function assertUniqueAreaNames(layout: TableLayout): void {
  const seen = new Set<string>()
  for (const area of layout.areas) {
    const key = area.name.trim().toLowerCase()
    if (seen.has(key)) throw conflict(`Ada dua area bernama "${area.name}".`)
    seen.add(key)
  }
}

function assertUniqueTableNames(layout: TableLayout): void {
  for (const area of layout.areas) {
    const seen = new Set<string>()
    for (const table of area.tables) {
      const key = table.name.trim().toLowerCase()
      if (seen.has(key)) {
        throw conflict(`Ada dua meja bernama "${table.name}" di area ${area.name}.`)
      }
      seen.add(key)
    }
  }
}
